/**
 * `normalizeDocument()` — the one function every renderer depends on.
 *
 * Takes a stored document row plus its lines and produces a flat, ordered,
 * render-ready model. Pure: no DOM, no database, no millimetres, no pixels,
 * no clock, no randomness. Given the same input it returns the same output,
 * which is what lets the printed invoice, the PDF, the thermal roll and the
 * on-screen editor be checked against each other.
 *
 * WHY THIS EXISTS. 1.6.x had eleven document types and eleven layouts. The
 * invoice layout was wrong — misaligned vertical borders, tax that did not
 * match the lines — and nobody could fix it without touching every other
 * layout. Here there is ONE model. A renderer is a template over this shape
 * and contains no arithmetic at all.
 *
 * IT REFUSES TO LIE. The stored header and the stored lines can disagree,
 * especially in data migrated from 1.6.x. Rendering a tax summary that does
 * not add up to the header would put a wrong number on a legal document, so
 * this function throws instead. A document that cannot be rendered correctly
 * must not be rendered at all.
 *
 * WHY THE GROUPS ARE COMPUTED HERE. The GST summary (one row per rate) and the
 * HSN summary (one row per code) are both statutory parts of a tax invoice.
 * Deriving them in a renderer is precisely how they drift apart between the
 * screen and the print, so they are derived once, here, and asserted against
 * the header.
 */
import { asMoney, asQuantity } from '../money/types';
import type { Money } from '../money/types';
import type {
  HsnSummaryRow,
  NormalizedCharge,
  NormalizedDocument,
  NormalizedLine,
  NormalizedParty,
  TaxSummaryRow,
  TaxSplitKind,
} from './types';

/** The subset of a stored line this function reads. */
export interface NormalizableLine {
  readonly lineNo: number;
  readonly productId?: string;
  readonly productName?: string;
  readonly description: string;
  readonly descriptionExtra?: string;
  readonly hsnSac?: string;
  readonly unit: string;
  readonly quantityMilli: number;
  readonly freeQuantityMilli: number;
  readonly ratePaise: number;
  readonly discountPaise: number;
  readonly taxablePaise: number;
  readonly taxRateBp: number;
  readonly cgstRateBp: number;
  readonly sgstRateBp: number;
  readonly igstRateBp: number;
  readonly cessRateBp: number;
  readonly cgstPaise: number;
  readonly sgstPaise: number;
  readonly igstPaise: number;
  readonly cessPaise: number;
  readonly totalPaise: number;
}

/** The subset of a stored charge this function reads. */
export interface NormalizableCharge {
  readonly label: string;
  readonly taxable: boolean;
  readonly amountPaise: number;
}

/** The subset of a stored document header this function reads. */
export interface NormalizableDocument {
  readonly docType: string;
  readonly docNumber: string;
  readonly docDate: string;
  readonly dueDate?: string;
  readonly expectedDate?: string;
  readonly financialYear: string;
  readonly status: string;
  readonly partySnapshot: NormalizedParty;
  readonly placeOfSupplyCode: string;
  readonly deliveryAddressLines?: string[];
  readonly deliveryStateName?: string;
  readonly supplyType: string;
  readonly grossSubtotalPaise: number;
  readonly totalDiscountPaise: number;
  readonly taxablePaise: number;
  readonly taxableChargesPaise: number;
  readonly nonTaxableChargesPaise: number;
  readonly cgstPaise: number;
  readonly sgstPaise: number;
  readonly igstPaise: number;
  readonly cessPaise: number;
  readonly tcsPaise: number;
  readonly taxTotalPaise: number;
  readonly preRoundOffPaise: number;
  readonly roundOffPaise: number;
  readonly grandTotalPaise: number;
  readonly totalQtyMilli: number;
  readonly amountInWords: string;
  readonly charges: readonly NormalizableCharge[];
  readonly notes?: string;
  readonly terms?: string;
  readonly declaration?: string;
  readonly coverLetter?: string;
  readonly coverLetterEnabled?: boolean;
  readonly expiryDate?: string;
  readonly calcVersion: string;
}

export interface NormalizeInput {
  readonly document: NormalizableDocument;
  readonly lines: readonly NormalizableLine[];
  /** The issuing business's own state code, to decide INTRA vs INTER. */
  readonly businessStateCode: string;
}

/** Raised when the stored header and the stored lines cannot both be true. */
export class DocumentDoesNotReconcileError extends Error {
  constructor(
    readonly docNumber: string,
    readonly field: string,
    readonly expected: number,
    readonly actual: number,
  ) {
    super(
      `Document ${docNumber}: ${field} on the header is ${actual} paise but the ` +
        `lines sum to ${expected} paise. Refusing to render a document whose ` +
        'tax summary would contradict its own totals.',
    );
    this.name = 'DocumentDoesNotReconcileError';
  }
}

/** Which legs are actually populated. Drives the column headings. */
function taxSplitOf(d: NormalizableDocument, lines: readonly NormalizableLine[]): TaxSplitKind {
  const cgst = d.cgstPaise !== 0 || lines.some((l) => l.cgstPaise !== 0);
  const igst = d.igstPaise !== 0 || lines.some((l) => l.igstPaise !== 0);
  const cess = d.cessPaise !== 0 || lines.some((l) => l.cessPaise !== 0);
  if (cgst && igst) {
    // A document carrying both legs is not renderable as a tax invoice: there
    // is no correct heading. Say so rather than picking one.
    throw new Error(
      `Document ${d.docNumber} carries both CGST/SGST and IGST. ` +
        'The tax split is ambiguous, so the document cannot be rendered.',
    );
  }
  if (igst) return 'IGST';
  if (cgst) return 'CGST_SGST';
  if (cess) return 'CESS_ONLY';
  return 'NONE';
}

/**
 * One row per distinct tax rate, in the order the rates first appear.
 *
 * Insertion order, not sorted: a rate appearing on line 1 should stay on top
 * of the summary, because that is the order the reader is looking for.
 */
function buildTaxSummary(lines: readonly NormalizedLine[]): TaxSummaryRow[] {
  const byRate = new Map<number, TaxSummaryRow>();
  for (const l of lines) {
    const existing = byRate.get(l.taxRateBp);
    if (existing) {
      byRate.set(l.taxRateBp, {
        ...existing,
        taxablePaise: asMoney(existing.taxablePaise + l.taxablePaise),
        cgstPaise: asMoney(existing.cgstPaise + l.cgstPaise),
        sgstPaise: asMoney(existing.sgstPaise + l.sgstPaise),
        igstPaise: asMoney(existing.igstPaise + l.igstPaise),
        cessPaise: asMoney(existing.cessPaise + l.cessPaise),
        totalTaxPaise: asMoney(
          existing.totalTaxPaise + l.cgstPaise + l.sgstPaise + l.igstPaise + l.cessPaise,
        ),
      });
    } else {
      byRate.set(l.taxRateBp, {
        taxRateBp: l.taxRateBp,
        taxablePaise: asMoney(l.taxablePaise),
        cgstPaise: asMoney(l.cgstPaise),
        sgstPaise: asMoney(l.sgstPaise),
        igstPaise: asMoney(l.igstPaise),
        cessPaise: asMoney(l.cessPaise),
        totalTaxPaise: asMoney(l.cgstPaise + l.sgstPaise + l.igstPaise + l.cessPaise),
      });
    }
  }
  return [...byRate.values()];
}

/**
 * One row per distinct HSN/SAC code, in first-appearance order.
 *
 * Lines with no HSN are excluded: a summary row keyed on an empty string is
 * noise, and a missing HSN is a data problem to be fixed on the line, not
 * something to paper over with a blank summary row.
 */
function buildHsnSummary(lines: readonly NormalizableLine[]): HsnSummaryRow[] {
  const byHsn = new Map<string, HsnSummaryRow>();
  for (const l of lines) {
    const hsn = (l.hsnSac ?? '').trim();
    if (hsn === '') continue;
    const existing = byHsn.get(hsn);
    if (existing) {
      byHsn.set(hsn, {
        ...existing,
        quantityMilli: asQuantity(existing.quantityMilli + l.quantityMilli),
        taxablePaise: asMoney(existing.taxablePaise + l.taxablePaise),
        cgstPaise: asMoney(existing.cgstPaise + l.cgstPaise),
        sgstPaise: asMoney(existing.sgstPaise + l.sgstPaise),
        igstPaise: asMoney(existing.igstPaise + l.igstPaise),
        cessPaise: asMoney(existing.cessPaise + l.cessPaise),
        totalTaxPaise: asMoney(
          existing.totalTaxPaise + l.cgstPaise + l.sgstPaise + l.igstPaise + l.cessPaise,
        ),
      });
    } else {
      byHsn.set(hsn, {
        hsnSac: hsn,
        quantityMilli: asQuantity(l.quantityMilli),
        taxablePaise: asMoney(l.taxablePaise),
        cgstPaise: asMoney(l.cgstPaise),
        sgstPaise: asMoney(l.sgstPaise),
        igstPaise: asMoney(l.igstPaise),
        cessPaise: asMoney(l.cessPaise),
        totalTaxPaise: asMoney(l.cgstPaise + l.sgstPaise + l.igstPaise + l.cessPaise),
      });
    }
  }
  return [...byHsn.values()];
}

/**
 * Flatten a stored document into the model every renderer reads.
 *
 * @throws {DocumentDoesNotReconcileError} when the header's tax or taxable
 * total does not equal the sum of the lines. A tax invoice whose summary
 * table disagrees with its own grand total is a legal problem, not a cosmetic
 * one, so this is refused rather than rendered.
 */
export function normalizeDocument(input: NormalizeInput): NormalizedDocument {
  const { document: d } = input;
  if (input.lines.length === 0) {
    throw new Error(`Document ${d.docNumber} has no lines, so there is nothing to render.`);
  }

  const lines: NormalizedLine[] = [...input.lines]
    .sort((a, b) => a.lineNo - b.lineNo)
    .map((l) => ({
      lineNo: l.lineNo,
      // Carried through so an editable draft can map each line back to a
      // catalogue item. Optional because a line can legitimately have no
      // product - see the field's doc comment in `core/doc/types.ts`.
      ...(l.productId ? { productId: l.productId } : {}),
      // Product name snapshot for display in print/preview.
      ...(l.productName ? { productName: l.productName } : {}),
      description: l.description,
      ...(l.descriptionExtra ? { descriptionExtra: l.descriptionExtra } : {}),
      ...(l.hsnSac ? { hsnSac: l.hsnSac } : {}),
      unit: l.unit,
      quantityMilli: asQuantity(l.quantityMilli),
      freeQuantityMilli: asQuantity(l.freeQuantityMilli),
      ratePaise: asMoney(l.ratePaise),
      discountPaise: asMoney(l.discountPaise),
      taxablePaise: asMoney(l.taxablePaise),
      taxRateBp: l.taxRateBp,
      cgstRateBp: l.cgstRateBp,
      sgstRateBp: l.sgstRateBp,
      igstRateBp: l.igstRateBp,
      cessRateBp: l.cessRateBp,
      cgstPaise: asMoney(l.cgstPaise),
      sgstPaise: asMoney(l.sgstPaise),
      igstPaise: asMoney(l.igstPaise),
      cessPaise: asMoney(l.cessPaise),
      totalPaise: asMoney(l.totalPaise),
    }));

  const sum = (pick: (l: NormalizedLine) => Money): Money =>
    asMoney(lines.reduce((s, l) => s + pick(l), 0));

  // ── The checks. Each is a place 1.6.x could print a wrong number. ─────────
  const lineTaxable = sum((l) => l.taxablePaise);
  if (lineTaxable !== d.taxablePaise) {
    throw new DocumentDoesNotReconcileError(d.docNumber, 'taxablePaise', lineTaxable, d.taxablePaise);
  }
  for (const [field, expected] of [
    ['cgstPaise', sum((l) => l.cgstPaise)],
    ['sgstPaise', sum((l) => l.sgstPaise)],
    ['igstPaise', sum((l) => l.igstPaise)],
    ['cessPaise', sum((l) => l.cessPaise)],
  ] as const) {
    if (expected !== d[field]) {
      throw new DocumentDoesNotReconcileError(d.docNumber, field, expected, d[field]);
    }
  }
  const legTotal = asMoney(d.cgstPaise + d.sgstPaise + d.igstPaise + d.cessPaise);
  if (legTotal !== d.taxTotalPaise) {
    throw new DocumentDoesNotReconcileError(
      d.docNumber, 'taxTotalPaise', legTotal, d.taxTotalPaise,
    );
  }

  const taxSummary = buildTaxSummary(lines);
  const hsnSummary = buildHsnSummary(lines);

  const charges: NormalizedCharge[] = d.charges.map((c) => ({
    label: c.label,
    taxable: c.taxable,
    amountPaise: asMoney(c.amountPaise),
  }));

  return {
    docType: d.docType,
    docNumber: d.docNumber,
    docDate: d.docDate,
    ...(d.dueDate ? { dueDate: d.dueDate } : {}),
    ...(d.expectedDate ? { expectedDate: d.expectedDate } : {}),
    ...(d.expiryDate ? { expiryDate: d.expiryDate } : {}),
    financialYear: d.financialYear,
    status: d.status,
    party: d.partySnapshot,
    placeOfSupplyCode: d.placeOfSupplyCode,
    isInterState: d.supplyType === 'INTER' || d.supplyType === 'EXPORT',
    taxSplit: taxSplitOf(d, lines),
    // Delivery details are a straight pass-through of a snapshot the write path
    // already froze. Nothing here recomputes them: the place of SUPPLY is the
    // resolved `placeOfSupplyCode` above, and re-deriving it here from an
    // address would be a second opinion about a tax decision.
    ...(d.deliveryAddressLines && d.deliveryAddressLines.length > 0
      ? { deliveryAddressLines: [...d.deliveryAddressLines] }
      : {}),
    ...(d.deliveryStateName ? { deliveryStateName: d.deliveryStateName } : {}),
    lines,
    charges,
    totals: {
      grossSubtotalPaise: asMoney(d.grossSubtotalPaise),
      totalDiscountPaise: asMoney(d.totalDiscountPaise),
      taxablePaise: asMoney(d.taxablePaise),
      taxableChargesPaise: asMoney(d.taxableChargesPaise),
      nonTaxableChargesPaise: asMoney(d.nonTaxableChargesPaise),
      cgstPaise: asMoney(d.cgstPaise),
      sgstPaise: asMoney(d.sgstPaise),
      igstPaise: asMoney(d.igstPaise),
      cessPaise: asMoney(d.cessPaise),
      taxTotalPaise: asMoney(d.taxTotalPaise),
      tcsPaise: asMoney(d.tcsPaise),
      preRoundOffPaise: asMoney(d.preRoundOffPaise),
      roundOffPaise: asMoney(d.roundOffPaise),
      grandTotalPaise: asMoney(d.grandTotalPaise),
    },
    taxSummary,
    hsnSummary,
    totalQtyMilli: asQuantity(d.totalQtyMilli),
    amountInWords: d.amountInWords,
    ...(d.notes ? { notes: d.notes } : {}),
    ...(d.terms ? { terms: d.terms } : {}),
    ...(d.declaration ? { declaration: d.declaration } : {}),
    ...(d.coverLetter ? { coverLetter: d.coverLetter } : {}),
    ...(d.coverLetterEnabled !== undefined ? { coverLetterEnabled: d.coverLetterEnabled } : {}),
    calcVersion: d.calcVersion,
  };
}

